/* One Index entry as an aligned row.

   Alignment does the grouping here rather than borders, and there is exactly one
   reading axis: 1,320 bordered cards read as wallpaper, and a multi-column grid
   of equal-weight boxes gives the eye no cue whether to read across or down.

   The name column is a FIXED width, not content-sized. Each row is its own
   custom element with its own shadow root, so there is no shared grid (or
   subgrid) to size a column across siblings — a fixed value is what actually
   guarantees the names line up. 240px holds ~33 characters; measured on the live
   library only 43 of 1,320 names (3.3%) exceed that and wrap to a second line,
   with the description staying aligned to the name's first baseline.

   Secondary actions are disclosed differently per pointer and the two arms are a
   render branch, not a CSS one: a fine pointer gets the hover cluster, a coarse
   one gets a ⋮ that opens the workspace's action sheet. Rendering both and hiding
   one would put four more elements on every row of the app's longest list.

   Presentational: emits `open` / `hide` / `menu`; the workspace
   owns the data and the API calls. */

import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { iconActions, revealOnHover, shadowReset } from '../../design/base.js';
import type { Mention } from '../../core/types.js';
import { trimNamePrefix } from '../../../../packages/core/index.js';
import '../../component/g-button/g-button.js';
import '../../component/g-icon/g-icon.js';

@customElement('index-row')
export class IndexRow extends LitElement {
  static override styles = [
    shadowReset,
    iconActions,
    revealOnHover,
    css`
      :host {
        display: block;
        position: relative;
        /* The app's heaviest list by a wide margin: 1,359 rows and ~120,000px of
           scroll content, against the 27,000px ADR 0012 §7 called the worst screen.
           Off-screen rows skip layout and paint entirely.

           93px is the measured median row height, so the scrollbar does not lurch
           as rows come into range; the auto keyword lets a taller row correct its estimate
           once rendered and remember the correction. Same treatment as place-row. */
        content-visibility: auto;
        contain-intrinsic-size: auto 93px;
        /* One source of truth for the name column: the grid track below and the
           actions' left offset are both derived from it, so they cannot drift. */
        --name-col: 240px;
        /* Width the row reserves at its right edge for the action affordance, so a
           long description never runs under it. One icon button (ADR 0051),
           measured rather than derived — 15px icon + 2*4px pad + 2*1px transparent
           border. The border is easy to forget and makes the naive figure 23px,
           which leaves the description touching the icon. */
        --acts-col: 25px;
      }
      /* One ⋮: a 46px button whose box hangs --space-3 outside --row-pad-r, so
         what it occupies inside the text's edge is 46 - 12. */
      :host([touch]) {
        --acts-col: 34px;
      }
      /* The whole row is the open target: .main spans the full width so a click
         anywhere on the row triggers the primary action, with the actions overlaid
         at the far right. They can't be children of .main — a <button> cannot
         contain other buttons — hence the overlay.

         Grid rather than flex so the description's start edge is identical on
         every row. */
      .main {
        display: grid;
        grid-template-columns: var(--name-col) minmax(0, 1fr);
        gap: var(--space-3);
        /* First baselines share a line, so the name and its description read as
           one entry despite different sizes. */
        align-items: baseline;
        width: 100%;
        /* The horizontal inset is the gutter handover (ADR 0009): --row-pad-*
           is 12px on desktop (the container pays the other 24) and 16px on mobile,
           where the row is the only thing holding its text off the screen edge and
           its background, divider and press state go edge-to-edge. */
        padding: var(--space-2) 0;
        padding-left: var(--row-pad-l);
        /* Reserve the actions' strip so a long description never runs under them,
           and so revealing them on hover cannot shift the text. */
        padding-right: calc(var(--row-pad-r) + var(--acts-col) + var(--space-2));
        border: 0;
        background: none;
        color: inherit;
        font: inherit;
        font-family: var(--font-ui);
        text-align: left;
        cursor: pointer;
        transition: background-color 120ms ease;
      }
      /* Native-list divider: a full-bleed hairline reads as a list, where the
         tinted bands it replaced read as a page of them. Last row in a section
         has none; the .kind header below already separates sections. */
      :host(:not(:last-child)) .main {
        border-bottom: 1px solid var(--border);
      }
      /* --surface-2 is the system's hover surface (g-button); the highlight spans
         the full row width now that the row is full-bleed. Gated per the
         frontend's :hover rule — a touch device has no pointer-leave, so an
         unqualified rule leaves the last row tapped tinted indefinitely. */
      @media (hover: hover) {
        :host(:hover) .main {
          background: var(--surface-2);
        }
      }
      /* Ungated, unlike :hover above — :active does not stick on touch, which is
         the whole reason hover had to be fenced off from it. */
      .main:active {
        background: var(--press);
      }
      .main:focus-visible {
        outline: none;
        box-shadow: var(--ring);
      }
      .label {
        display: flex;
        align-items: baseline;
        gap: var(--space-2);
        min-width: 0;
      }
      .name {
        font-size: 0.9rem;
        font-weight: 500;
        line-height: 1.4;
        overflow-wrap: anywhere;
      }
      /* Dim, not accent. At ~100 visible rows a coloured badge on every
         multi-mention entry out-shouts the names it annotates — the count is an
         annotation and should read as one. Shown only at n >= 2: 1,204 of 1,320
         entries are n=1, so "x1" would be noise on nine rows in ten (§23). */
      .n {
        flex: none;
        color: var(--text-dim);
        font-family: var(--font-mono);
        font-size: 0.72rem;
      }
      .detail {
        color: var(--text-dim);
        font-size: 0.8rem;
        line-height: 1.4;
        overflow-wrap: anywhere;
      }
      /* Fine pointer: the two icon buttons centered at the row's right edge,
         revealed on hover (revealOnHover). Offset by --row-inset-r so it tracks
         the gutter handover; the container pays the rest. */
      .acts {
        position: absolute;
        top: 50%;
        right: calc(var(--row-inset-r) + var(--space-2));
        transform: translateY(-50%);
      }
      /* Centered on the name's first line rather than the row's mid-height, so a
         wrapped two-line entry does not push it down to the taller box's centre.

         Offset by the button's own padding so the ICON's edge lands on
         --row-pad-r (within the button's 1px transparent border), not the button
         box's. A 46px target whose glyph sits 12px inside it would otherwise read
         as 28px from the screen edge while the text opposite reads as 16px. The
         box stops --space-3 short of the edge and never overflows. */
      .trigger {
        display: flex;
        position: absolute;
        top: var(--space-2);
        right: calc(var(--row-pad-r) - var(--space-3));
        height: calc(0.9rem * 1.4);
        align-items: center;
        color: var(--text-dim);
        /* 20px icon + 2*12px pad + 2*1px transparent border = 46px, clear of the
           --touch-min floor. g-button's icon-only default is 8px pad, which
           paints 38px. */
        --icon-size: 20px;
        --g-button-pad: var(--space-3);
      }
      /* Below the two-column measure the name column stops earning its width:
         stack instead, which is also v1's shape. The actions need no change — they
         are anchored to the row's right edge, not to a track. */
      @media (max-width: 699px) {
        .main {
          grid-template-columns: minmax(0, 1fr);
          gap: 0;
        }
      }
    `,
  ];

  @property({ attribute: false }) entry!: Mention;

  /** Which arm to render. Set by the workspace, which reads the pointer once. */
  @property({ type: Boolean, reflect: true }) touch = false;

  private emit(type: 'open' | 'hide' | 'menu'): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true }));
  }

  override render() {
    const e = this.entry;
    if (!e) return html``;
    const detail = trimNamePrefix(e.name, e.detail);
    return html`
      <button class="main" @click=${() => this.emit('open')}>
        <span class="label">
          <span class="name">${e.name}</span>
          ${e.n > 1 ? html`<span class="n">×${e.n}</span>` : ''}
        </span>
        <span class="detail">${detail}</span>
      </button>
      ${this.touch
        ? html`
            <span class="trigger">
              <g-button icon-only label="Actions for ${e.name}" @click=${() => this.emit('menu')}>
                <g-icon name="more-vertical"></g-icon>
              </g-button>
            </span>
          `
        : html`
            <span class="acts">
              <g-button icon-only label="Hide" @click=${() => this.emit('hide')}>
                <g-icon name="eye-off"></g-icon>
              </g-button>
            </span>
          `}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'index-row': IndexRow;
  }
}
